import { useEffect, useRef } from "react";
import html2canvas from "html2canvas-pro";
import type { RefObject } from "react";
import { getPodcastBadgeLayout } from "../components/ranking-card/ProgramThumbWithPodcastBadge";
import {
  RANKING_ASSET_WIDTH_PX,
  RANKING_THUMB_PX,
  rankingThumbRadius,
} from "../components/ranking-card/rankingAssetDimensions";

export const PROGRAM_ASSET_CARD_SELECTOR = "[data-ranking-card]";

const S3_ORIGIN = "https://podscope.s3.amazonaws.com";

export const TOP10_TABLE_ASSET_WIDTH_PX = 1500;

/**
 * Instagram keeps a photo at its original width up to 1080px and sizes
 * anything wider down to 1080. Paint at 4×, then halve twice so the PNG
 * matches the 1080×1350 layout and Instagram does not resize it.
 */
export const PROGRAM_ASSET_OUTPUT_SCALE = 1;
const PROGRAM_ASSET_SUPERSAMPLE = 4;
const PROGRAM_ASSET_CAPTURE_SCALE =
  PROGRAM_ASSET_OUTPUT_SCALE * PROGRAM_ASSET_SUPERSAMPLE;

const CAPTURE_FONT_LOAD_SPECS = [
  '400 16px "DM Serif Text"',
  '400 32px "DM Serif Text"',
  '400 48px "DM Serif Text"',
  '400 64px "DM Serif Text"',
  '400 16px "DM Sans"',
  '600 16px "DM Sans"',
  '400 16px Inter',
  '500 16px Inter',
  '600 20px Inter',
  '700 42px Inter',
];

/** Canvas text uses the MAIN document's fonts — ensure faces are decoded before capture. */
async function ensureCaptureFontsReady(): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return;
  try {
    await Promise.all(
      CAPTURE_FONT_LOAD_SPECS.map((spec) =>
        document.fonts.load(spec).catch(() => undefined),
      ),
    );
    await document.fonts.ready;
  } catch {
    /* ignore */
  }
}

/** PNG-only: normalize font-variant in the clone (weights left as-is to match the page). */
function applyCaptureTextStyles(cloned: HTMLElement): void {
  cloned.style.fontVariant = "normal";
  Array.from(cloned.querySelectorAll<HTMLElement>("*")).forEach((el) => {
    el.style.fontVariant = "normal";
  });
}

const CAPTURE_ELLIPSIS = "…";
/** Padding/descenders can make scrollHeight a few px taller than the clamp box. */
const LINE_CLAMP_OVERFLOW_TOLERANCE_PX = 4;

function getLineClampValue(el: HTMLElement, view: Window): number | null {
  if (el.hasAttribute("data-ranking-capture-full-text")) return null;
  const raw = view.getComputedStyle(el).webkitLineClamp;
  if (!raw || raw === "none") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function elementUsesEllipsisOrLineClamp(
  el: HTMLElement,
  view: Window,
): boolean {
  if (el.hasAttribute("data-ranking-capture-full-text")) return false;
  if (getLineClampValue(el, view) != null) return true;
  return view.getComputedStyle(el).textOverflow === "ellipsis";
}

/** PNG capture titles: unwrap truncation styles in the html2canvas clone. */
function prepareRankingCaptureFullText(
  cloned: HTMLElement,
): void {
  cloned
    .querySelectorAll<HTMLElement>("[data-ranking-capture-full-text]")
    .forEach((el) => {
      el.style.overflow = "visible";
      el.style.textOverflow = "clip";
      el.style.whiteSpace = "normal";
      el.style.display = "block";
      el.style.webkitLineClamp = "unset";
      el.style.webkitBoxOrient = "initial";
      el.style.width = "auto";
      el.style.maxWidth = "none";
      el.style.flex = "1 1 auto";

      const nameColumn = el.parentElement;
      if (nameColumn) {
        nameColumn.style.overflow = "visible";
        nameColumn.style.width = "auto";
        nameColumn.style.maxWidth = "none";
        nameColumn.style.flex = "1 1 0";
      }
    });
}

/** Pin flex text columns to live layout widths so html2canvas matches on-screen ellipsis. */
function lockCaptureElementWidth(el: HTMLElement, widthPx: number): void {
  if (widthPx <= 0) return;
  const w = `${widthPx}px`;
  el.style.width = w;
  el.style.maxWidth = w;
  el.style.minWidth = w;
  el.style.flex = "0 0 auto";
  el.style.boxSizing = "border-box";
}

/**
 * Chrome reports `display: -webkit-box` as `flow-root`, so html2canvas-pro's
 * line-clamp renderer never runs. Bake a literal ellipsis into overflowing
 * clone text so the PNG matches the on-screen 2-line clamp.
 */
function materializeLineClampEllipsisInClone(
  sourceEl: HTMLElement,
  cloneEl: HTMLElement,
  sourceView: Window,
): void {
  const lineClamp = getLineClampValue(sourceEl, sourceView);
  if (lineClamp == null) return;
  if (
    sourceEl.scrollHeight <=
    sourceEl.clientHeight + LINE_CLAMP_OVERFLOW_TOLERANCE_PX
  ) {
    return;
  }

  const fullText = (cloneEl.textContent ?? "").replace(/\s+/g, " ").trim();
  if (!fullText) return;

  const maxHeight = sourceEl.clientHeight;
  lockCaptureElementWidth(cloneEl, sourceEl.offsetWidth);

  cloneEl.style.display = "block";
  cloneEl.style.webkitLineClamp = "unset";
  cloneEl.style.webkitBoxOrient = "initial";
  cloneEl.style.overflow = "hidden";
  cloneEl.style.textOverflow = "clip";
  cloneEl.style.whiteSpace = "normal";
  cloneEl.style.maxHeight = `${maxHeight}px`;
  cloneEl.style.height = `${maxHeight}px`;

  let lo = 0;
  let hi = fullText.length;
  let best = 0;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    cloneEl.textContent = `${fullText.slice(0, mid)}${CAPTURE_ELLIPSIS}`;
    if (
      cloneEl.scrollHeight <=
      maxHeight + LINE_CLAMP_OVERFLOW_TOLERANCE_PX
    ) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  cloneEl.textContent =
    best > 0
      ? `${fullText.slice(0, best)}${CAPTURE_ELLIPSIS}`
      : CAPTURE_ELLIPSIS;
}

/**
 * html2canvas under-measures flex item widths, which breaks `-webkit-line-clamp` /
 * `text-overflow: ellipsis`. Copy measured widths from the live card into the clone,
 * and materialize multi-line ellipsis text (Chrome/html2canvas gap).
 */
function syncRankingCaptureEllipsisWidths(
  source: HTMLElement,
  cloned: HTMLElement,
): void {
  const sourceView = source.ownerDocument.defaultView;
  if (!sourceView) return;
  const liveView = sourceView;

  function walk(sourceEl: HTMLElement, cloneEl: HTMLElement): void {
    if (elementUsesEllipsisOrLineClamp(sourceEl, liveView)) {
      lockCaptureElementWidth(cloneEl, sourceEl.offsetWidth);
      const sourceParent = sourceEl.parentElement;
      const cloneParent = cloneEl.parentElement;
      if (sourceParent && cloneParent) {
        lockCaptureElementWidth(cloneParent, sourceParent.offsetWidth);
      }
      materializeLineClampEllipsisInClone(sourceEl, cloneEl, liveView);
    }

    const sourceChildren = Array.from(sourceEl.children) as HTMLElement[];
    const cloneChildren = Array.from(cloneEl.children) as HTMLElement[];
    const len = Math.min(sourceChildren.length, cloneChildren.length);
    for (let i = 0; i < len; i++) {
      walk(sourceChildren[i], cloneChildren[i]);
    }
  }

  walk(source, cloned);
}

/** Drop leftover alpha so a mail client cannot matte holes onto white. */
function flattenOpaque(
  source: HTMLCanvasElement,
  backgroundColor: string,
): HTMLCanvasElement {
  const out = document.createElement("canvas");
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext("2d");
  if (!ctx) return source;
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(source, 0, 0);
  return out;
}

/** Halve the supersampled render until it matches the layout size. */
function downscaleCanvas(
  source: HTMLCanvasElement,
  factor: number,
): HTMLCanvasElement {
  if (factor <= 1) return source;
  let current = source;
  let remaining = factor;
  while (remaining > 1.001) {
    const step = remaining >= 2 ? 2 : remaining;
    const target = document.createElement("canvas");
    target.width = Math.max(1, Math.round(current.width / step));
    target.height = Math.max(1, Math.round(current.height / step));
    const ctx = target.getContext("2d");
    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(current, 0, 0, target.width, target.height);
    }
    current = target;
    remaining /= step;
  }
  return current;
}

export type ProgramAssetCaptureOptions = {
  width?: number;
  height?: number;
  backgroundColor?: string;
};

async function fetchToDataUrl(src: string): Promise<string | null> {
  try {
    const res = await fetch(src, { mode: "cors", cache: "no-store" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function paintS3SrcToCanvasMap(
  src: string,
  srcToCanvas: Map<string, HTMLCanvasElement>,
): Promise<void> {
  if (!src || src.startsWith("data:") || srcToCanvas.has(src) || !src.startsWith("http")) return;
  let dataUrl = await fetchToDataUrl(src);
  if (!dataUrl && src.startsWith(S3_ORIGIN)) {
    const altSrc = src.endsWith(".jpg")
      ? src.replace(/\.jpg$/, ".png")
      : src.endsWith(".png")
        ? src.replace(/\.png$/, ".jpg")
        : null;
    if (altSrc) dataUrl = await fetchToDataUrl(altSrc);
  }
  if (!dataUrl) return;
  const resolvedDataUrl = dataUrl;

  await new Promise<void>((resolve) => {
    const paintImg = new Image();
    paintImg.onload = () => {
      const c = document.createElement("canvas");
      c.width = paintImg.naturalWidth || 52;
      c.height = paintImg.naturalHeight || 52;
      const ctx = c.getContext("2d");
      if (ctx) ctx.drawImage(paintImg, 0, 0);
      srcToCanvas.set(src, c);
      resolve();
    };
    paintImg.onerror = () => resolve();
    paintImg.src = resolvedDataUrl;
  });
}

async function buildS3ImageCanvasMap(
  element: HTMLElement,
): Promise<Map<string, HTMLCanvasElement>> {
  const imgEls = Array.from(
    element.querySelectorAll("img"),
  ) as HTMLImageElement[];
  const thumbSvgSrcs = Array.from(
    element.querySelectorAll<SVGElement>("svg[data-ranking-program-thumb-svg]"),
  ).flatMap((svg) => [
    svg.getAttribute("data-cover-src") || "",
    svg.getAttribute("data-badge-src") || "",
  ]);
  const srcToCanvas = new Map<string, HTMLCanvasElement>();

  await Promise.all([
    ...imgEls.map((img) => paintS3SrcToCanvasMap(img.src, srcToCanvas)),
    ...thumbSvgSrcs.map((src) => paintS3SrcToCanvasMap(src, srcToCanvas)),
  ]);

  return srcToCanvas;
}

/** Resolve preloaded canvas; try exact src then S3 origin match (query-string drift). */
function resolvePreloadedCanvas(
  src: string,
  srcToCanvas: Map<string, HTMLCanvasElement>,
): HTMLCanvasElement | null {
  if (!src) return null;
  const exact = srcToCanvas.get(src);
  if (exact) return exact;
  const srcBase = src.split("?")[0];
  for (const [key, canvas] of Array.from(srcToCanvas.entries())) {
    if (key === src) return canvas;
    // Same S3 object, different cache-buster query.
    const keyBase = key.split("?")[0];
    if (keyBase && keyBase === srcBase) return canvas;
  }
  return null;
}

function drawCoverImage(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  destWidth: number,
  destHeight: number,
): void {
  const scale = Math.max(destWidth / sourceWidth, destHeight / sourceHeight);
  const dw = sourceWidth * scale;
  const dh = sourceHeight * scale;
  ctx.drawImage(source, (destWidth - dw) / 2, (destHeight - dh) / 2, dw, dh);
}

/**
 * Punch the podcast-badge gap out of a cover layer only.
 * destination-out on the final canvas would leave transparent holes, and mail
 * clients composite those onto white as a bright border around each badge.
 */
function punchPodcastBadgeCutout(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
  thumbPx: number = RANKING_THUMB_PX,
): void {
  const scaleX = canvasW / thumbPx;
  const scaleY = canvasH / thumbPx;
  const { cutout } = getPodcastBadgeLayout(thumbPx);
  const x = cutout.x * scaleX;
  const y = cutout.y * scaleY;
  const sizeX = cutout.size * scaleX;
  const sizeY = cutout.size * scaleY;
  const rx = cutout.rx * Math.min(scaleX, scaleY);

  ctx.save();
  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, sizeX, sizeY, rx);
  } else {
    ctx.rect(x, y, sizeX, sizeY);
  }
  ctx.fill();
  ctx.restore();
}

function drawRoundedCoverImage(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  destWidth: number,
  destHeight: number,
  radius: number,
): void {
  ctx.save();
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(0, 0, destWidth, destHeight, radius);
  } else {
    ctx.rect(0, 0, destWidth, destHeight);
  }
  ctx.clip();
  drawCoverImage(ctx, source, sourceWidth, sourceHeight, destWidth, destHeight);
  ctx.restore();
}

function positivePx(value: string | undefined): number {
  const parsed = Number.parseFloat(value ?? "");
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function parseSvgViewBox(svg: SVGElement): { x: number; y: number; w: number; h: number } {
  const parts = (svg.getAttribute("viewBox") ?? "")
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length === 4 && parts.every(Number.isFinite)) {
    return { x: parts[0]!, y: parts[1]!, w: parts[2]!, h: parts[3]! };
  }
  return { x: 0, y: 0, w: 0, h: 0 };
}

function svgAttr(el: Element, name: string): string | null {
  return el.getAttribute(name) ?? el.getAttribute(name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`));
}

function cssPaintValue(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed === "none" || trimmed === "transparent") return null;
  return trimmed;
}

function paintSvgNode(ctx: CanvasRenderingContext2D, el: Element): void {
  const tag = el.tagName.toLowerCase();
  if (tag === "defs" || tag === "clippath" || tag === "title" || tag === "desc") {
    return;
  }
  if (tag === "g" || tag === "svg" || tag === "a") {
    ctx.save();
    Array.from(el.children).forEach((child) => paintSvgNode(ctx, child));
    ctx.restore();
    return;
  }

  const view = el.ownerDocument.defaultView;
  const cs = view?.getComputedStyle(el as Element);
  const fillAttr = svgAttr(el, "fill");
  const strokeAttr = svgAttr(el, "stroke");
  const fill = cssPaintValue(fillAttr ?? cs?.fill);
  const stroke = cssPaintValue(strokeAttr ?? cs?.stroke);
  const strokeWidth = Number.parseFloat(
    svgAttr(el, "stroke-width") ??
      svgAttr(el, "strokeWidth") ??
      cs?.strokeWidth ??
      "",
  );
  const opacity = Number.parseFloat(svgAttr(el, "opacity") ?? cs?.opacity ?? "1");
  const fillOpacity = Number.parseFloat(
    svgAttr(el, "fill-opacity") ?? svgAttr(el, "fillOpacity") ?? "1",
  );
  const strokeOpacity = Number.parseFloat(
    svgAttr(el, "stroke-opacity") ?? svgAttr(el, "strokeOpacity") ?? "1",
  );
  ctx.save();
  if (Number.isFinite(opacity)) ctx.globalAlpha *= opacity;
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth =
      Number.isFinite(strokeWidth) && strokeWidth > 0 ? strokeWidth : 1;
    const join = svgAttr(el, "stroke-linejoin") ?? cs?.strokeLinejoin ?? "";
    const cap = svgAttr(el, "stroke-linecap") ?? cs?.strokeLinecap ?? "";
    if (join === "round" || join === "bevel" || join === "miter") ctx.lineJoin = join;
    if (cap === "round" || cap === "butt" || cap === "square") ctx.lineCap = cap;
  }

  if (tag === "path") {
    const d = svgAttr(el, "d");
    if (d) {
      const path = new Path2D(d);
      if (fill) {
        ctx.save();
        if (Number.isFinite(fillOpacity)) ctx.globalAlpha *= fillOpacity;
        ctx.fillStyle = fill;
        ctx.fill(path);
        ctx.restore();
      }
      if (stroke) {
        ctx.save();
        if (Number.isFinite(strokeOpacity)) ctx.globalAlpha *= strokeOpacity;
        // Stroke-only band edges must stay visible after opaque fills.
        ctx.stroke(path);
        ctx.restore();
      }
    }
  } else if (tag === "line") {
    ctx.beginPath();
    ctx.moveTo(Number(svgAttr(el, "x1") ?? 0), Number(svgAttr(el, "y1") ?? 0));
    ctx.lineTo(Number(svgAttr(el, "x2") ?? 0), Number(svgAttr(el, "y2") ?? 0));
    if (stroke) ctx.stroke();
  } else if (tag === "circle") {
    const cx = Number(svgAttr(el, "cx") ?? 0);
    const cy = Number(svgAttr(el, "cy") ?? 0);
    const r = Number(svgAttr(el, "r") ?? 0);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    if (fill) {
      ctx.save();
      if (Number.isFinite(fillOpacity)) ctx.globalAlpha *= fillOpacity;
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.restore();
    }
    if (stroke) ctx.stroke();
  } else if (tag === "rect") {
    const x = Number(svgAttr(el, "x") ?? 0);
    const y = Number(svgAttr(el, "y") ?? 0);
    const w = Number(svgAttr(el, "width") ?? 0);
    const h = Number(svgAttr(el, "height") ?? 0);
    const rx = Number(svgAttr(el, "rx") ?? 0);
    ctx.beginPath();
    if (rx > 0 && typeof ctx.roundRect === "function") ctx.roundRect(x, y, w, h, rx);
    else ctx.rect(x, y, w, h);
    if (fill) {
      ctx.save();
      if (Number.isFinite(fillOpacity)) ctx.globalAlpha *= fillOpacity;
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.restore();
    }
    if (stroke) ctx.stroke();
  } else if (tag === "text") {
    const x = Number(svgAttr(el, "x") ?? 0);
    const y = Number(svgAttr(el, "y") ?? 0);
    const size = svgAttr(el, "font-size") ?? "12";
    const weight = svgAttr(el, "font-weight") ?? "400";
    const family = svgAttr(el, "font-family") ?? "sans-serif";
    const anchor = svgAttr(el, "text-anchor");
    ctx.font = `${weight} ${size}px ${family}`;
    ctx.textAlign =
      anchor === "middle" ? "center" : anchor === "end" ? "end" : "start";
    ctx.textBaseline = "alphabetic";
    if (fill && fill !== "none") ctx.fillStyle = fill;
    ctx.fillText((el.textContent ?? "").trim(), x, y);
  }
  ctx.restore();
}

/**
 * html2canvas serializes <svg> to a data-URL image and drawImage() of that
 * taints the output (web fonts / SVG-as-image). Paint primitives onto a
 * same-origin canvas instead.
 */
function rasterizeSvgElement(
  svg: SVGElement,
  captureScale: number,
  cssW: number,
  cssH: number,
  doc: Document,
): HTMLCanvasElement | null {
  if (cssW <= 0 || cssH <= 0) return null;
  const canvas = doc.createElement("canvas");
  canvas.width = Math.max(1, Math.round(cssW * captureScale));
  canvas.height = Math.max(1, Math.round(cssH * captureScale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const vb = parseSvgViewBox(svg);
  const unitsW = vb.w > 0 ? vb.w : cssW;
  const unitsH = vb.h > 0 ? vb.h : cssH;
  ctx.setTransform(canvas.width / unitsW, 0, 0, canvas.height / unitsH, 0, 0);
  if (vb.w > 0) ctx.translate(-vb.x, -vb.y);
  paintSvgNode(ctx, svg);
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  canvas.style.display = "block";
  canvas.style.overflow = "visible";
  return canvas;
}

function isRankingThumbSvg(el: Element): boolean {
  return el.hasAttribute("data-ranking-program-thumb-svg");
}

function isRankingThumbMedia(img: HTMLImageElement): boolean {
  return (
    img.hasAttribute("data-ranking-cover-cutout") ||
    img.hasAttribute("data-ranking-podcast-badge") ||
    Boolean(img.closest("svg[data-ranking-program-thumb-svg]"))
  );
}

function thumbPxFromSvg(svg: SVGElement): number {
  const thumbPxAttr = Number(svg.getAttribute("data-thumb-px"));
  return Number.isFinite(thumbPxAttr) && thumbPxAttr > 0
    ? thumbPxAttr
    : RANKING_THUMB_PX;
}

function canvasToCloneImg(
  source: HTMLCanvasElement,
  cloneDoc: Document,
  cssText: string,
): HTMLImageElement {
  const img = cloneDoc.createElement("img");
  img.alt = "";
  img.src = source.toDataURL("image/png");
  img.style.cssText = cssText;
  return img;
}

function layoutInRoot(
  el: Element,
  root: HTMLElement,
): { x: number; y: number; w: number; h: number } {
  const rootRect = root.getBoundingClientRect();
  const rect = el.getBoundingClientRect();
  const scaleX = root.offsetWidth / Math.max(1, rootRect.width);
  const scaleY = root.offsetHeight / Math.max(1, rootRect.height);
  return {
    x: (rect.left - rootRect.left) * scaleX,
    y: (rect.top - rootRect.top) * scaleY,
    w: rect.width * scaleX,
    h: rect.height * scaleY,
  };
}

function isCardBackgroundImg(img: HTMLImageElement): boolean {
  return (img.currentSrc || img.src).includes("ranking-bg");
}

function hidePinnedCaptureText(cloned: HTMLElement): void {
  cloned.querySelectorAll<HTMLElement>("[data-capture-pin]").forEach((pin) => {
    pin.querySelectorAll<HTMLElement>("*").forEach((el) => {
      const nested = Array.from(el.children).some((child) => child.tagName !== "BR");
      if (nested || !(el.textContent || "").trim()) return;
      el.style.setProperty("color", "transparent", "important");
      el.style.setProperty("-webkit-text-fill-color", "transparent", "important");
    });
  });
}

function rectInRoot(
  rect: DOMRect,
  root: HTMLElement,
): { x: number; y: number; w: number; h: number } {
  const rootRect = root.getBoundingClientRect();
  const scaleX = root.offsetWidth / Math.max(1, rootRect.width);
  const scaleY = root.offsetHeight / Math.max(1, rootRect.height);
  return {
    x: (rect.left - rootRect.left) * scaleX,
    y: (rect.top - rootRect.top) * scaleY,
    w: rect.width * scaleX,
    h: rect.height * scaleY,
  };
}

/** html2canvas reflows the metrics grid, so redraw that text at the live boxes. */
function paintPinnedText(
  ctx: CanvasRenderingContext2D,
  element: HTMLElement,
  scale: number,
): void {
  element.querySelectorAll<HTMLElement>("[data-capture-pin] *").forEach((el) => {
    if (!(el instanceof HTMLElement) || el.closest("svg")) return;
    const nested = Array.from(el.children).some((child) => child.tagName !== "BR");
    const lines = (el.innerText || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (nested || lines.length === 0) return;
    const cs = window.getComputedStyle(el);
    const fontSize = Number.parseFloat(cs.fontSize) || 16;
    ctx.save();
    ctx.fillStyle = cs.color;
    ctx.font = `${cs.fontWeight} ${fontSize * scale}px ${cs.fontFamily}`;
    ctx.textBaseline = "middle";
    const align = cs.textAlign;
    ctx.textAlign =
      align === "right" || align === "end"
        ? "right"
        : align === "center"
          ? "center"
          : "left";
    const box = rectInRoot(el.getBoundingClientRect(), element);
    const lineH = box.h / lines.length;
    lines.forEach((line, index) => {
      const x =
        ctx.textAlign === "right"
          ? (box.x + box.w) * scale
          : ctx.textAlign === "center"
            ? (box.x + box.w / 2) * scale
            : box.x * scale;
      ctx.fillText(line, x, (box.y + lineH * index + lineH / 2) * scale);
    });
    ctx.restore();
  });
}

function pinCaptureBoxes(source: HTMLElement, cloned: HTMLElement): void {
  const livePins = Array.from(source.querySelectorAll<HTMLElement>("[data-capture-pin]"));
  const clonePins = Array.from(cloned.querySelectorAll<HTMLElement>("[data-capture-pin]"));
  livePins.forEach((live, index) => {
    const clone = clonePins[index];
    if (!clone) return;
    clone.style.setProperty("position", "relative", "important");
    clone.style.setProperty("display", "block", "important");
    clone.style.width = `${live.offsetWidth}px`;
    clone.style.height = `${live.offsetHeight}px`;
    pinCaptureChildren(live, clone);
  });
}

/** Freeze each child to the box the browser already measured, so capture cannot reflow the grid. */
function pinCaptureChildren(live: HTMLElement, clone: HTMLElement): void {
  const origin = live.getBoundingClientRect();
  const scaleX = live.offsetWidth / Math.max(1, origin.width);
  const scaleY = live.offsetHeight / Math.max(1, origin.height);
  const liveKids = Array.from(live.children) as HTMLElement[];
  const cloneKids = Array.from(clone.children) as HTMLElement[];
  liveKids.forEach((child, index) => {
    const cloneChild = cloneKids[index];
    if (!cloneChild) return;
    const rect = child.getBoundingClientRect();
    cloneChild.style.setProperty("position", "absolute", "important");
    cloneChild.style.setProperty("margin", "0", "important");
    cloneChild.style.left = `${(rect.left - origin.left) * scaleX}px`;
    cloneChild.style.top = `${(rect.top - origin.top) * scaleY}px`;
    cloneChild.style.width = `${Math.max(1, rect.width * scaleX)}px`;
    cloneChild.style.height = `${Math.max(1, rect.height * scaleY)}px`;
    cloneChild.style.right = "auto";
    cloneChild.style.bottom = "auto";
    if (child.tagName.toLowerCase() !== "svg" && child.children.length > 0) {
      pinCaptureChildren(child, cloneChild);
    }
  });
}

function replaceMediaWithPlaceholders(
  source: HTMLElement,
  cloned: HTMLElement,
): void {
  const doc = cloned.ownerDocument;
  const sourceImgs = Array.from(source.querySelectorAll("img"));
  Array.from(cloned.querySelectorAll("img")).forEach((img, index) => {
    if (!img.parentElement) return;
    if (
      img.hasAttribute("data-ranking-cover-cutout") ||
      img.hasAttribute("data-ranking-podcast-badge")
    ) {
      return;
    }
    const live = sourceImgs[index];
    const measured = live ? window.getComputedStyle(live) : null;
    const placeholder = doc.createElement("div");
    placeholder.style.display = "block";
    placeholder.style.flexShrink = "0";
    const pinned = img.style.position === "absolute" && img.style.left;
    if (pinned) {
      placeholder.style.position = "absolute";
      placeholder.style.left = img.style.left;
      placeholder.style.top = img.style.top;
      placeholder.style.width = img.style.width;
      placeholder.style.height = img.style.height;
      placeholder.style.margin = "0";
    } else if (measured?.position === "absolute") {
      placeholder.style.position = "absolute";
      placeholder.style.inset = "0";
      placeholder.style.width = "100%";
      placeholder.style.height = "100%";
    } else {
      placeholder.style.width = `${live?.offsetWidth || 0}px`;
      placeholder.style.height = `${live?.offsetHeight || 0}px`;
      if (measured?.margin) placeholder.style.margin = measured.margin;
    }
    img.parentElement.replaceChild(placeholder, img);
  });

  const sourceSvgs = Array.from(source.querySelectorAll("svg")).filter(
    (el) => !isRankingThumbSvg(el),
  );
  Array.from(cloned.querySelectorAll("svg"))
    .filter((el) => !isRankingThumbSvg(el))
    .forEach((svg, index) => {
      if (!svg.parentElement) return;
      const live = sourceSvgs[index];
      const vb = parseSvgViewBox(svg);
      const placeholder = doc.createElement("div");
      const pinned = svg.style.position === "absolute" && svg.style.left;
      placeholder.style.display = "block";
      placeholder.style.flexShrink = "0";
      if (pinned) {
        placeholder.style.position = "absolute";
        placeholder.style.left = svg.style.left;
        placeholder.style.top = svg.style.top;
        placeholder.style.width = svg.style.width;
        placeholder.style.height = svg.style.height;
        placeholder.style.margin = "0";
      } else {
        placeholder.style.width = `${live?.clientWidth || vb.w}px`;
        placeholder.style.height = `${live?.clientHeight || vb.h}px`;
      }
      svg.parentElement.replaceChild(placeholder, svg);
    });
}

function paintLiveMediaOntoCanvas(
  ctx: CanvasRenderingContext2D,
  element: HTMLElement,
  srcToCanvas: Map<string, HTMLCanvasElement>,
  scale: number,
): void {
  // Lines first, then photos. Chart logos sit on top of the series.
  Array.from(element.querySelectorAll("svg")).forEach((svg) => {
    if (isRankingThumbSvg(svg)) return;
    const box = layoutInRoot(svg, element);
    const painted = rasterizeSvgElement(svg, 1, box.w, box.h, document);
    if (!painted) return;
    ctx.drawImage(
      painted,
      box.x * scale,
      box.y * scale,
      box.w * scale,
      box.h * scale,
    );
  });

  Array.from(element.querySelectorAll("img")).forEach((img) => {
    if (isCardBackgroundImg(img) || isRankingThumbMedia(img)) return;
    const bmp = resolvePreloadedCanvas(img.currentSrc || img.src, srcToCanvas);
    if (!bmp) return;
    const box = layoutInRoot(img, element);
    if (box.w <= 0 || box.h <= 0) return;
    const x = box.x * scale;
    const y = box.y * scale;
    const w = box.w * scale;
    const h = box.h * scale;
    const cs = window.getComputedStyle(img);
    const parentCs = img.parentElement
      ? window.getComputedStyle(img.parentElement)
      : null;
    const radiusToken =
      cs.borderRadius && cs.borderRadius !== "0px"
        ? cs.borderRadius
        : parentCs?.borderRadius || "0px";
    const radius = Number.parseFloat(radiusToken) || 0;
    const isCircle =
      radiusToken.includes("50%") || radius >= Math.min(box.w, box.h) / 2;
    const hasDropShadow = `${parentCs?.filter ?? ""} ${cs.filter}`.includes(
      "drop-shadow",
    );
    const borderW = Math.max(
      Number.parseFloat(cs.borderWidth) || 0,
      Number.parseFloat(cs.borderTopWidth) || 0,
      Number.parseFloat(cs.borderRightWidth) || 0,
      Number.parseFloat(cs.borderBottomWidth) || 0,
      Number.parseFloat(cs.borderLeftWidth) || 0,
      Number.parseFloat(img.getAttribute("data-share-logo-border-width") || "") ||
        0,
    );
    const borderColor =
      img.getAttribute("data-share-logo-border-color") ||
      cs.borderTopColor ||
      cs.borderColor ||
      "";
    const borderStyle =
      cs.borderStyle || cs.borderTopStyle || cs.borderLeftStyle || "";
    const path = () => {
      ctx.beginPath();
      if (isCircle) {
        ctx.arc(x + w / 2, y + h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
      } else if (radius > 0 && typeof ctx.roundRect === "function") {
        ctx.roundRect(x, y, w, h, radius * scale);
      } else {
        ctx.rect(x, y, w, h);
      }
    };
    ctx.save();
    if (hasDropShadow) {
      ctx.shadowColor = "rgba(0,0,0,0.55)";
      ctx.shadowBlur = 8 * scale;
      ctx.shadowOffsetY = 3 * scale;
    }
    path();
    ctx.clip();
    ctx.translate(x, y);
    drawCoverImage(ctx, bmp, bmp.width, bmp.height, w, h);
    ctx.restore();
    if (borderW > 0 && borderStyle !== "none" && borderColor) {
      ctx.save();
      path();
      ctx.lineWidth = Math.max(borderW, 2) * scale;
      ctx.strokeStyle = borderColor;
      ctx.stroke();
      ctx.restore();
    }
  });
}

/** Paint program thumbs at design radius. Live <img> overlay would flatten them. */
function paintRankingThumbsOntoCanvas(
  ctx: CanvasRenderingContext2D,
  element: HTMLElement,
  srcToCanvas: Map<string, HTMLCanvasElement>,
  scale: number,
): void {
  Array.from(
    element.querySelectorAll<SVGElement>("svg[data-ranking-program-thumb-svg]"),
  ).forEach((svg) => {
    const thumbPx = thumbPxFromSvg(svg);
    const layout = getPodcastBadgeLayout(thumbPx);
    const box = layoutInRoot(svg, element);
    if (box.w <= 0 || box.h <= 0 || layout.view.w <= 0 || layout.view.h <= 0) {
      return;
    }
    const unitX = box.w / layout.view.w;
    const unitY = box.h / layout.view.h;
    const coverBmp = resolvePreloadedCanvas(
      svg.getAttribute("data-cover-src") || "",
      srcToCanvas,
    );
    const badgeBmp = resolvePreloadedCanvas(
      svg.getAttribute("data-badge-src") || "",
      srcToCanvas,
    );
    if (coverBmp) {
      const x = (box.x + layout.capture.cover.x * unitX) * scale;
      const y = (box.y + layout.capture.cover.y * unitY) * scale;
      const w = layout.thumb * unitX * scale;
      const h = layout.thumb * unitY * scale;
      const coverCanvas = document.createElement("canvas");
      coverCanvas.width = Math.max(1, Math.round(w));
      coverCanvas.height = Math.max(1, Math.round(h));
      const coverCtx = coverCanvas.getContext("2d");
      if (coverCtx) {
        coverCtx.imageSmoothingEnabled = true;
        coverCtx.imageSmoothingQuality = "high";
        coverCtx.beginPath();
        if (typeof coverCtx.roundRect === "function") {
          coverCtx.roundRect(
            0,
            0,
            coverCanvas.width,
            coverCanvas.height,
            rankingThumbRadius(thumbPx) * unitX * scale,
          );
        } else {
          coverCtx.rect(0, 0, coverCanvas.width, coverCanvas.height);
        }
        coverCtx.clip();
        drawCoverImage(
          coverCtx,
          coverBmp,
          coverBmp.width,
          coverBmp.height,
          coverCanvas.width,
          coverCanvas.height,
        );
        punchPodcastBadgeCutout(coverCtx, coverCanvas.width, coverCanvas.height, thumbPx);
        ctx.drawImage(coverCanvas, x, y, w, h);
      }
    }
    if (badgeBmp) {
      const x = (box.x + layout.capture.badge.x * unitX) * scale;
      const y = (box.y + layout.capture.badge.y * unitY) * scale;
      const w = layout.badge.size * unitX * scale;
      const h = layout.badge.size * unitY * scale;
      ctx.save();
      ctx.beginPath();
      if (typeof ctx.roundRect === "function") {
        ctx.roundRect(x, y, w, h, layout.badge.rx * unitX * scale);
      } else {
        ctx.rect(x, y, w, h);
      }
      ctx.clip();
      ctx.translate(x, y);
      drawCoverImage(ctx, badgeBmp, badgeBmp.width, badgeBmp.height, w, h);
      ctx.restore();
    }
  });
}

function applyCloneCaptureStyles(
  source: HTMLElement,
  cloned: HTMLElement,
  width: number,
  height: number | undefined,
  srcToCanvas: Map<string, HTMLCanvasElement>,
  captureScale: number,
): void {
  applyCaptureTextStyles(cloned);
  prepareRankingCaptureFullText(cloned);
  syncRankingCaptureEllipsisWidths(source, cloned);
  cloned.style.position = "relative";
  cloned.style.top = "0";
  cloned.style.left = "0";
  cloned.style.transform = "none";
  cloned.style.opacity = "1";
  cloned.style.width = `${width}px`;
  if (height != null) {
    cloned.style.height = `${height}px`;
    cloned.style.minHeight = `${height}px`;
    cloned.style.maxHeight = `${height}px`;
  }
  cloned.style.boxSizing = "border-box";
  cloned.style.backgroundColor = "transparent";
  cloned.style.backgroundImage = "none";

  if (cloned.parentElement) {
    cloned.parentElement.style.cssText = [
      "position:fixed",
      "top:0",
      "left:0",
      `width:${width}px`,
      height != null ? `height:${height}px` : "",
      "overflow:visible",
      "transform:none",
      "opacity:1",
      "z-index:0",
      "pointer-events:none",
    ]
      .filter(Boolean)
      .join(";");
  }

  Array.from(cloned.querySelectorAll<HTMLElement>("*")).forEach((el) => {
    if (el.style.opacity === "0") el.style.opacity = "1";
    if (el.style.visibility === "hidden") el.style.visibility = "visible";
  });

  // html2canvas-pro clone omits browser UA stylesheet resets, so <p> and
  // headings pick up UA margins. Inline margin:0 also wipes designed margins
  // (section gaps). Pin the live computed box so the PNG matches the page.
  const sourceTextBlocks = Array.from(
    source.querySelectorAll<HTMLElement>("p, h1, h2, h3, h4, h5, h6"),
  );
  Array.from(
    cloned.querySelectorAll<HTMLElement>("p, h1, h2, h3, h4, h5, h6"),
  ).forEach((block, index) => {
    const live = sourceTextBlocks[index];
    const cs = live?.ownerDocument.defaultView?.getComputedStyle(live);
    if (!cs) {
      block.style.margin = "0";
      block.style.padding = "0";
      return;
    }
    block.style.marginTop = cs.marginTop;
    block.style.marginRight = cs.marginRight;
    block.style.marginBottom = cs.marginBottom;
    block.style.marginLeft = cs.marginLeft;
    block.style.paddingTop = cs.paddingTop;
    block.style.paddingRight = cs.paddingRight;
    block.style.paddingBottom = cs.paddingBottom;
    block.style.paddingLeft = cs.paddingLeft;
  });

  // Unified thumb SVG (cover cutout + badge) → HTML/canvas stack html2canvas can paint.
  // Prefer data-*-src attrs: onclone often strips foreignObject <img> nodes/srcs.
  Array.from(
    cloned.querySelectorAll<SVGElement>("svg[data-ranking-program-thumb-svg]"),
  ).forEach((svgEl) => {
    if (!svgEl.parentElement) return;
    const coverSrc =
      svgEl.getAttribute("data-cover-src") ||
      svgEl.querySelector<HTMLImageElement>("img[data-ranking-cover-cutout]")
        ?.src ||
      "";
    const badgeSrc =
      svgEl.getAttribute("data-badge-src") ||
      svgEl.querySelector<HTMLImageElement>("img[data-ranking-podcast-badge]")
        ?.src ||
      "";
    const coverCanvas = resolvePreloadedCanvas(coverSrc, srcToCanvas);
    const badgeCanvas = resolvePreloadedCanvas(badgeSrc, srcToCanvas);
    if (!coverCanvas && !badgeCanvas) return;

    const thumbPx = thumbPxFromSvg(svgEl);
    const { thumb, badge, view, capture } = getPodcastBadgeLayout(thumbPx);
    // Clone may report clientWidth 0 before layout — always use design size.
    const cssW = thumb;
    const coverRadius = rankingThumbRadius(thumbPx);
    const parent = svgEl.parentElement;
    const contained = svgEl.hasAttribute("data-contained");
    // Keep the hanging badge inside the border box. html2canvas clips
    // overflow and negative-margin overflow — a previous marginBottom
    // pull-up cropped the channel logo and shifted the stack vs the title.
    parent.style.boxSizing = "content-box";
    parent.style.width = `${view.w}px`;
    parent.style.height = `${view.h}px`;
    parent.style.overflow = "hidden";
    parent.style.borderRadius = "0";
    parent.style.background = "transparent";
    parent.style.flexShrink = "0";
    if (contained) {
      parent.style.marginLeft = "0";
      parent.style.marginTop = "0";
      parent.style.marginBottom = "0";
    } else {
      parent.style.marginLeft = `${view.x}px`;
      parent.style.marginTop = "0";
      parent.style.marginBottom = "0";
      parent.style.alignSelf = "flex-start";
    }

    const wrap = document.createElement("div");
    wrap.setAttribute("data-ranking-program-thumb-capture", "");
    wrap.style.cssText = `position:relative;width:${view.w}px;height:${view.h}px;overflow:hidden;flex-shrink:0;`;

    if (coverCanvas) {
      const cover = document.createElement("canvas");
      const coverPx = Math.max(1, Math.round(cssW * captureScale));
      cover.width = coverPx;
      cover.height = coverPx;
      const coverCtx = cover.getContext("2d");
      if (coverCtx) {
        coverCtx.imageSmoothingEnabled = true;
        coverCtx.imageSmoothingQuality = "high";
        drawRoundedCoverImage(
          coverCtx,
          coverCanvas,
          coverCanvas.width,
          coverCanvas.height,
          coverPx,
          coverPx,
          (coverRadius * coverPx) / cssW,
        );
        punchPodcastBadgeCutout(coverCtx, coverPx, coverPx, thumbPx);
      }
      cover.style.cssText =
        `position:absolute;left:${capture.cover.x}px;top:${capture.cover.y}px;width:${cssW}px;height:${cssW}px;display:block;border-radius:${coverRadius}px;`;
      wrap.appendChild(
        canvasToCloneImg(cover, cloned.ownerDocument, cover.style.cssText),
      );
    }

    if (badgeCanvas) {
      const badgeEl = document.createElement("canvas");
      const badgeCss = badge.size;
      const badgePx = Math.max(1, Math.round(badgeCss * captureScale));
      badgeEl.width = badgePx;
      badgeEl.height = badgePx;
      const badgeCtx = badgeEl.getContext("2d");
      if (badgeCtx) {
        badgeCtx.imageSmoothingEnabled = true;
        badgeCtx.imageSmoothingQuality = "high";
        drawRoundedCoverImage(
          badgeCtx,
          badgeCanvas,
          badgeCanvas.width,
          badgeCanvas.height,
          badgePx,
          badgePx,
          (badge.rx * badgePx) / badgeCss,
        );
      }
      badgeEl.setAttribute("data-ranking-podcast-badge", "");
      badgeEl.style.cssText = [
        "position:absolute",
        `left:${capture.badge.x}px`,
        `top:${capture.badge.y}px`,
        `width:${badgeCss}px`,
        `height:${badgeCss}px`,
        "display:block",
        `border-radius:${badge.rx}px`,
      ].join(";");
      wrap.appendChild(
        canvasToCloneImg(badgeEl, cloned.ownerDocument, badgeEl.style.cssText),
      );
    }

    parent.replaceChild(wrap, svgEl);
  });

  replaceMediaWithPlaceholders(source, cloned);
  pinCaptureBoxes(source, cloned);
  hidePinnedCaptureText(cloned);
}

export function resolveProgramAssetCardElement(
  root: HTMLElement | null | undefined,
): HTMLElement | null {
  if (!root) return null;
  if (root.matches(PROGRAM_ASSET_CARD_SELECTOR)) return root;
  return root.querySelector<HTMLElement>(PROGRAM_ASSET_CARD_SELECTOR);
}

export async function captureProgramAssetCardToBlob(
  element: HTMLElement,
  options: ProgramAssetCaptureOptions = {},
): Promise<Blob | null> {
  const width = options.width ?? RANKING_ASSET_WIDTH_PX;
  const height = options.height;
  const backgroundColor = options.backgroundColor ?? "#1a1a1a";
  const captureScale = PROGRAM_ASSET_CAPTURE_SCALE;
  const srcToCanvas = await buildS3ImageCanvasMap(element);
  await ensureCaptureFontsReady();

  const rendered = await html2canvas(element, {
    scale: captureScale,
    useCORS: false,
    allowTaint: false,
    width,
    ...(height != null ? { height } : {}),
    backgroundColor: null,
    logging: false,
    onclone: (_clonedDoc, cloned) => {
      applyCloneCaptureStyles(
        element,
        cloned,
        width,
        height,
        srcToCanvas,
        captureScale,
      );
    },
  });

  const composed = document.createElement("canvas");
  composed.width = rendered.width;
  composed.height = rendered.height;
  const composedCtx = composed.getContext("2d");
  if (!composedCtx) {
    throw new Error("Failed to compose program asset PNG");
  }
  composedCtx.fillStyle = backgroundColor;
  composedCtx.fillRect(0, 0, composed.width, composed.height);
  const bgImg = Array.from(element.querySelectorAll("img")).find(isCardBackgroundImg);
  const bgCanvas = bgImg
    ? resolvePreloadedCanvas(bgImg.currentSrc || bgImg.src, srcToCanvas)
    : null;
  if (bgCanvas) {
    drawCoverImage(
      composedCtx,
      bgCanvas,
      bgCanvas.width,
      bgCanvas.height,
      composed.width,
      composed.height,
    );
  }
  composedCtx.drawImage(rendered, 0, 0);
  paintLiveMediaOntoCanvas(composedCtx, element, srcToCanvas, captureScale);
  paintRankingThumbsOntoCanvas(composedCtx, element, srcToCanvas, captureScale);
  paintPinnedText(composedCtx, element, captureScale);

  const canvas = flattenOpaque(
    downscaleCanvas(composed, PROGRAM_ASSET_SUPERSAMPLE),
    backgroundColor,
  );
  return await new Promise<Blob | null>((resolve, reject) => {
    try {
      canvas.toBlob(resolve, "image/png");
    } catch (err) {
      reject(err);
    }
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadProgramAssetPng(
  root: HTMLElement | null | undefined,
  filename: string,
  options: ProgramAssetCaptureOptions = {},
): Promise<void> {
  const card = resolveProgramAssetCardElement(root);
  if (!card) {
    throw new Error("Program asset card element not found");
  }
  const blob = await captureProgramAssetCardToBlob(card, options);
  if (!blob) {
    throw new Error("Failed to capture program asset PNG");
  }
  downloadBlob(blob, filename);
}

/** Hidden off-screen shell for screenshot-variant cards (iOS loads images in viewport). */
export const hiddenProgramAssetCaptureShellSx = {
  position: "fixed",
  top: 0,
  left: 0,
  opacity: 0,
  zIndex: -1,
  pointerEvents: "none",
} as const;

export function useProgramAssetAutoDownload(
  cardRef: RefObject<HTMLElement | null>,
  shouldAutoDownload: boolean,
  isReady: boolean,
  filename: string,
  options: ProgramAssetCaptureOptions = {},
): void {
  const downloadedRef = useRef(false);
  const width = options.width ?? RANKING_ASSET_WIDTH_PX;
  const height = options.height;

  useEffect(() => {
    if (!shouldAutoDownload || !isReady || downloadedRef.current) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled || downloadedRef.current) return;
      try {
        await downloadProgramAssetPng(cardRef.current, filename, {
          width,
          height,
          backgroundColor: options.backgroundColor,
        });
        downloadedRef.current = true;
        if (window.parent !== window) {
          window.parent.postMessage(
            { type: "program-asset-png-downloaded" },
            window.location.origin,
          );
        }
      } catch (err) {
        console.error("Auto-download program asset PNG failed:", err);
        if (window.parent !== window) {
          window.parent.postMessage(
            { type: "program-asset-png-download-failed" },
            window.location.origin,
          );
        }
      }
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    shouldAutoDownload,
    isReady,
    filename,
    cardRef,
    width,
    height,
    options.backgroundColor,
  ]);
}
